import { Box, Typography, Stack, Button, IconButton, ToggleButtonGroup } from "@mui/joy";
import { useFormContext } from "react-hook-form";
import { FormInput } from "@/components/forms/FormInput";
import { FormDate } from "@/components/forms/FormDate";
import { FormSelect } from "@/components/forms/FormSelect";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { calculateDailyIntake } from "../utils/dailyIntake";

export function NutritionProfileForm({ autoAdd = false }) {
  const form = useFormContext();
  const [searchParams] = useSearchParams();
  const shouldAutoAdd = searchParams.get('addNutritionProfile') === 'true' || autoAdd;
  const nutritionProfile = form.watch('nutrition_profile');
  const hasNutritionProfile = !!nutritionProfile;
  const profileType = nutritionProfile?.type || 'standard';
  const birthdayInputRef = useRef(null);
  const containerRef = useRef(null);
  const lastAutoAddRef = useRef(false);

  function handleAddNutritionProfile() {
    const currentYear = new Date().getFullYear();
    const defaultBirthYear = currentYear - 30;
    const defaultBirthday = `${defaultBirthYear}-01-01`;
    
    form.setValue('nutrition_profile', {
      type: 'standard',
      standard: {
        birthday: defaultBirthday,
        sex: 'male',
        height: 175,
        weight: 75,
        activity_level: 'moderately_active',
      },
      advanced: null,
    });
    setTimeout(() => {
      scrollAndFocus();
    }, 0);
  }

  function handleSwitchType(newType) {
    if (!hasNutritionProfile) return;
    
    const currentProfile = form.getValues('nutrition_profile');
    
    if (newType === 'advanced') {
      const calculated = calculateDailyIntake(currentProfile);
      form.setValue('nutrition_profile', {
        type: 'advanced',
        standard: currentProfile.standard,
        advanced: {
          kcal: calculated.kcal,
          protein: calculated.protein,
          fat: calculated.fat,
          carbs: calculated.carbs,
          fiber: calculated.fiber,
        },
      });
    } else {
      form.setValue('nutrition_profile', {
        ...currentProfile,
        type: newType,
      });
    }
  }

  function scrollAndFocus() {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }    
    if (birthdayInputRef.current) {
      const input = birthdayInputRef.current.querySelector('input');
      if (input) {
        input.focus();
      }
    }
  }

  useEffect(() => {
    if (shouldAutoAdd && shouldAutoAdd !== lastAutoAddRef.current) {
      lastAutoAddRef.current = shouldAutoAdd;
      setTimeout(() => {
        const currentProfile = form.getValues('nutrition_profile');
        if (!currentProfile) {
          handleAddNutritionProfile();
        } else {
          scrollAndFocus();
        }
      }, 0);
    } else if (!shouldAutoAdd) {
      lastAutoAddRef.current = false;
    }
  }, [shouldAutoAdd]);

  useEffect(() => {
    if (hasNutritionProfile && shouldAutoAdd && lastAutoAddRef.current) {
      scrollAndFocus();
    }
  }, [hasNutritionProfile, shouldAutoAdd]);

  function handleRemoveNutritionProfile() {
    form.setValue('nutrition_profile', null);
    form.clearErrors('nutrition_profile');    
  }

  const sexOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' },
  ];

  const activityLevelOptions = [
    { value: 'sedentary', label: 'Sedentary' },
    { value: 'lightly_active', label: 'Lightly Active' },
    { value: 'moderately_active', label: 'Moderately Active' },
    { value: 'very_active', label: 'Very Active' },
    { value: 'extremely_active', label: 'Extremely Active' },
  ];

  return (
    <Box ref={containerRef}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography level="title-md" sx={{ mb: 0.5 }}>Nutrition Profile</Typography>
          <Typography level="body-sm" sx={{ mb: 2, color: 'var(--joy-palette-neutral-500)' }}>
            {hasNutritionProfile ? 'Provide your information to get personalized daily nutrition recommendations' : 'Add your nutrition profile to get personalized daily nutrition recommendations' }
          </Typography>          
        </Box>
        {hasNutritionProfile && (
          <IconButton color="danger" onClick={handleRemoveNutritionProfile} size="sm">
            <CloseOutlinedIcon />
          </IconButton>          
        )}
      </Stack>
      {!hasNutritionProfile && <Button variant="outlined" onClick={handleAddNutritionProfile}>Add Nutrition Profile</Button>}            

      {hasNutritionProfile && (
        <Stack gap={2.5} sx={{ mt: 1, width: '100%' }}>
          <ToggleButtonGroup
            size="sm"
            value={profileType}            
            onChange={(_, newValue) => handleSwitchType(newValue)}
            sx={{ width: { xs: '100%', sm: 'fit-content' }, mb: 1 }}
          >
            <Button value="standard" sx={{ flexGrow: 1 }}>Standard</Button>
            <Button value="advanced" sx={{ flexGrow: 1 }}>Advanced</Button>
          </ToggleButtonGroup>

          {profileType === 'standard' && (
            <Stack gap={2.5}>
              <Box ref={birthdayInputRef}>
                <FormDate name="nutrition_profile.standard.birthday" label="Birthday" />
              </Box>
              <FormSelect name="nutrition_profile.standard.sex" label="Gender" options={sexOptions} />
              <FormInput name="nutrition_profile.standard.height" label="Height (cm)" type="number" />
              <FormInput name="nutrition_profile.standard.weight" label="Weight (kg)" type="number" />
              <FormSelect name="nutrition_profile.standard.activity_level" label="Activity Level" options={activityLevelOptions} />
            </Stack>
          )}

          {profileType === 'advanced' && (
            <Stack gap={2.5}>
              <FormInput name="nutrition_profile.advanced.kcal" label="Calories (kcal)" type="number" inputProps={{ step: 'any' }} />
              <FormInput name="nutrition_profile.advanced.protein" label="Protein (g)" type="number" inputProps={{ step: 'any' }} />
              <FormInput name="nutrition_profile.advanced.fat" label="Fat (g)" type="number" inputProps={{ step: 'any' }} />
              <FormInput name="nutrition_profile.advanced.carbs" label="Carbs (g)" type="number" inputProps={{ step: 'any' }} />
              <FormInput name="nutrition_profile.advanced.fiber" label="Fiber (g)" type="number" inputProps={{ step: 'any' }} />
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}

